// src/components/admin/page-management/SectionForm.js
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBorderAll,
  faCog,
  faDesktop,
  faLayerGroup,
  faMagic,
  faMobileAlt,
  faPalette,
  faTabletAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import BlogPostsForm from "./section-forms/BlogPostsForm";
import ButtonForm from "./section-forms/ButtonForm";
import ColumnsForm from "./section-forms/ColumnsForm";
import ContactFormSectionForm from "./section-forms/ContactFormSectionForm";
import ContainerForm from "./section-forms/ContainerForm";
import CtaForm from "./section-forms/CtaForm";
import FeaturedProductsForm from "./section-forms/FeaturedProductsForm";
import FeaturesForm from "./section-forms/FeaturesForm";
import GalleryForm from "./section-forms/GalleryForm";
import GoogleMapsForm from "./section-forms/GoogleMapsForm";
import GridItemForm from "./section-forms/GridItemForm";
import HeaderDividedForm from "./section-forms/HeaderDividedForm";
import HeroForm from "./section-forms/HeroForm";
import ImageForm from "./section-forms/ImageForm";
import NewsletterForm from "./section-forms/NewsletterForm";
import PopupForm from "./section-forms/PopupForm";
import ProductCatalogForm from "./section-forms/ProductCatalogForm";
import ProductCatalogVerticalForm from "./section-forms/ProductCatalogVerticalForm";
import RichTextForm from "./section-forms/RichTextForm";
import SlideshowForm from "./section-forms/SlideshowForm";
import SlideshowHeaderWaveForm from "./section-forms/SlideShowHeaderWaveForm";
import SlideshowLinearForm from "./section-forms/SlideshowLinearForm";
import SpacerForm from "./section-forms/SpacerForm";
import StaticContentForm from "./section-forms/StaticContentForm";
import StatisticsForm from "./section-forms/StatisticsForm";
import TeamMemberForm from "./section-forms/TeamMemberForm";
import TestimonialsForm from "./section-forms/TestimonialsForm";
import TextForm from "./section-forms/TextForm";

const SectionForm = ({
  section,
  setSection,
  onCancel,
  onSave,
  buttonText = "Save",
  pageSlug,
}) => {
  // Add debugging for pageSlug
  useEffect(() => {
    console.log("SectionForm received pageSlug:", pageSlug);
  }, [pageSlug]);

  // State for active tab in settings
  const [activeSettingsTab, setActiveSettingsTab] = useState("layout");

  // State for expanded settings sections
  const [expandedSections, setExpandedSections] = useState({
    layout: true,
    appearance: false,
    background: false,
    typography: false,
    border: false,
    animation: false,
    advanced: false,
  });

  // Toggle a settings section
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Set active tab
  const setTab = (tab) => {
    setActiveSettingsTab(tab);
    // Auto-expand the related section
    let sectionToExpand = tab;
    if (tab === "desktop" || tab === "tablet" || tab === "mobile") {
      sectionToExpand = "layout";
    }
    setExpandedSections({
      ...expandedSections,
      [sectionToExpand]: true,
    });
  };

  const updateContent = (field, value) => {
    setSection({
      ...section,
      content: {
        ...section.content,
        [field]: value,
      },
    });
  };

  const updateSettings = (field, value) => {
    setSection({
      ...section,
      settings: {
        ...section.settings,
        [field]: value,
      },
    });
  };

  // Update responsive settings
  const updateResponsiveSettings = (device, field, value) => {
    const currentResponsive = section.settings?.responsiveSettings || {
      desktop: {},
      tablet: {},
      mobile: {},
    };

    setSection({
      ...section,
      settings: {
        ...section.settings,
        responsiveSettings: {
          ...currentResponsive,
          [device]: {
            ...currentResponsive[device],
            [field]: value,
          },
        },
      },
    });
  };

  // Get value from responsive settings with fallback to main settings
  const getResponsiveValue = (device, field, defaultValue = "") => {
    const responsiveSettings = section.settings?.responsiveSettings || {};
    const deviceSettings = responsiveSettings[device] || {};

    // First check if there's a device-specific value
    if (deviceSettings[field] !== undefined) {
      return deviceSettings[field];
    }

    // Otherwise use main settings or default
    return section.settings?.[field] !== undefined
      ? section.settings[field]
      : defaultValue;
  };

  // Render appropriate form based on section type
  const renderSectionForm = () => {
    console.log(
      `Rendering form for section type: ${section.type} with pageSlug: ${pageSlug}`
    );

    switch (section.type) {
      case "hero":
        return (
          <HeroForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "headerDivided":
        return (
          <HeaderDividedForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "text":
        return (
          <TextForm content={section.content} updateContent={updateContent} />
        );

      case "button":
        return (
          <ButtonForm content={section.content} updateContent={updateContent} />
        );

      case "image":
        return (
          <ImageForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "richText":
        return (
          <RichTextForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "gallery":
        return (
          <GalleryForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "cta":
        return (
          <CtaForm content={section.content} updateContent={updateContent} />
        );

      case "columns":
        return (
          <ColumnsForm
            content={section.content}
            updateContent={updateContent}
          />
        );

      case "spacer":
        return (
          <SpacerForm content={section.content} updateContent={updateContent} />
        );

      case "container":
        return (
          <ContainerForm
            content={section.content}
            updateContent={updateContent}
          />
        );

      case "grid":
        return (
          <GridItemForm
            content={section.content}
            updateContent={updateContent}
          />
        );

      case "slideshow":
        return (
          <SlideshowForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "slideshowHeaderWave":
        return (
          <SlideshowHeaderWaveForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "slideshowLinear":
        return (
          <SlideshowLinearForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "teamMembers":
        return (
          <TeamMemberForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "productCatalog":
        return (
          <ProductCatalogForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "productCatalogVertical":
        return (
          <ProductCatalogVerticalForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "staticContent":
        return (
          <StaticContentForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "featuredProducts":
        return (
          <FeaturedProductsForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "statistics":
        return (
          <StatisticsForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "blogPosts":
        return (
          <BlogPostsForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "features":
        return (
          <FeaturesForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "newsletter":
        return (
          <NewsletterForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "testimonials":
        return (
          <TestimonialsForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "contactForm":
        return (
          <ContactFormSectionForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "googleMaps":
        return (
          <GoogleMapsForm
            content={section.content}
            updateContent={updateContent}
            pageSlug={pageSlug}
          />
        );

      case "popup":
        return <PopupForm section={section} setSection={setSection} />;
      default:
        return <p>No form available for section type: {section.type}</p>;
    }
  };

  // Render settings based on active tab
  const renderSettings = () => {
    switch (activeSettingsTab) {
      case "layout":
        return renderLayoutSettings();
      case "appearance":
        return renderAppearanceSettings();
      case "desktop":
      case "tablet":
      case "mobile":
        return renderResponsiveSettings(activeSettingsTab);
      case "advanced":
        return renderAdvancedSettings();
      default:
        return renderLayoutSettings();
    }
  };

  // Layout settings section
  const renderLayoutSettings = () => (
    <div>
      {/* Layout Section */}
      <div className={`mb-4 ${expandedSections.layout ? "" : "opacity-75"}`}>
        <div
          className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
          onClick={() => toggleSection("layout")}
        >
          <h5 className="font-medium flex items-center">
            <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
            Layout
          </h5>
          <span>{expandedSections.layout ? "▼" : "►"}</span>
        </div>

        {expandedSections.layout && (
          <div className="p-3 border-l border-r border-b rounded-b mb-2">
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={section.settings?.fullWidth || false}
                  onChange={(e) =>
                    updateSettings("fullWidth", e.target.checked)
                  }
                  className="mr-2"
                />
                <span>Full Width Section</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1">Width</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="100% or 1200px"
                  value={section.content?.width || ""}
                  onChange={(e) => updateContent("width", e.target.value)}
                />
              </div>
              <div>
                <label className="block mb-1">Max Width</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="1200px"
                  value={section.content?.maxWidth || ""}
                  onChange={(e) => updateContent("maxWidth", e.target.value)}
                />
              </div>
              <div>
                <label className="block mb-1">Height</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="auto or 500px"
                  value={section.content?.height || ""}
                  onChange={(e) => updateContent("height", e.target.value)}
                />
              </div>
              <div>
                <label className="block mb-1">Min Height</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="300px"
                  value={section.content?.minHeight || ""}
                  onChange={(e) => updateContent("minHeight", e.target.value)}
                />
              </div>
            </div>

            {/* Padding Controls */}
            <div className="mb-4">
              <label className="block mb-1">Padding</label>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Top"
                    value={section.content?.paddingTop || ""}
                    onChange={(e) =>
                      updateContent("paddingTop", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Top</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Right"
                    value={section.content?.paddingRight || ""}
                    onChange={(e) =>
                      updateContent("paddingRight", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Right</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Bottom"
                    value={section.content?.paddingBottom || ""}
                    onChange={(e) =>
                      updateContent("paddingBottom", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Bottom</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Left"
                    value={section.content?.paddingLeft || ""}
                    onChange={(e) =>
                      updateContent("paddingLeft", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Left</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter values like 20px, 2rem, etc.
              </p>
            </div>

            {/* Margin Controls */}
            <div className="mb-4">
              <label className="block mb-1">Margin</label>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Top"
                    value={section.content?.marginTop || ""}
                    onChange={(e) => updateContent("marginTop", e.target.value)}
                  />
                  <span className="text-xs text-center block mt-1">Top</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Right"
                    value={section.content?.marginRight || ""}
                    onChange={(e) =>
                      updateContent("marginRight", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Right</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Bottom"
                    value={section.content?.marginBottom || ""}
                    onChange={(e) =>
                      updateContent("marginBottom", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Bottom</span>
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full p-2 border rounded text-center"
                    placeholder="Left"
                    value={section.content?.marginLeft || ""}
                    onChange={(e) =>
                      updateContent("marginLeft", e.target.value)
                    }
                  />
                  <span className="text-xs text-center block mt-1">Left</span>
                </div>
              </div>
            </div>

            {/* Positioning */}
            <div className="mb-4">
              <label className="block mb-1">Position</label>
              <select
                className="w-full p-2 border rounded"
                value={section.settings?.position || "static"}
                onChange={(e) => updateSettings("position", e.target.value)}
              >
                <option value="static">Static (Default)</option>
                <option value="relative">Relative</option>
                <option value="absolute">Absolute</option>
                <option value="fixed">Fixed</option>
                <option value="sticky">Sticky</option>
              </select>
            </div>

            {section.settings?.position &&
              section.settings.position !== "static" && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-1">Top</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      placeholder="0"
                      value={section.content?.top || ""}
                      onChange={(e) => updateContent("top", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Right</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      placeholder="0"
                      value={section.content?.right || ""}
                      onChange={(e) => updateContent("right", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Bottom</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      placeholder="0"
                      value={section.content?.bottom || ""}
                      onChange={(e) => updateContent("bottom", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Left</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      placeholder="0"
                      value={section.content?.left || ""}
                      onChange={(e) => updateContent("left", e.target.value)}
                    />
                  </div>
                </div>
              )}

            <div className="mb-4">
              <label className="block mb-1">Z-Index</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                placeholder="0"
                value={section.settings?.zIndex || "0"}
                onChange={(e) => updateSettings("zIndex", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-1">Overflow</label>
              <select
                className="w-full p-2 border rounded"
                value={section.settings?.overflow || "visible"}
                onChange={(e) => updateSettings("overflow", e.target.value)}
              >
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
                <option value="scroll">Scroll</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Appearance settings section
  const renderAppearanceSettings = () => (
    <div>
      {/* Background Settings */}
      <div
        className={`mb-4 ${expandedSections.background ? "" : "opacity-75"}`}
      >
        <div
          className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
          onClick={() => toggleSection("background")}
        >
          <h5 className="font-medium flex items-center">
            <FontAwesomeIcon icon={faPalette} className="mr-2" />
            Background
          </h5>
          <span>{expandedSections.background ? "▼" : "►"}</span>
        </div>

        {expandedSections.background && (
          <div className="p-3 border-l border-r border-b rounded-b mb-2">
            <div className="mb-4">
              <label className="block mb-1">Background Color</label>
              <div className="flex items-center">
                <input
                  type="color"
                  className="w-10 h-10 p-1 border rounded mr-2"
                  value={section.content?.customBackgroundColor || "#ffffff"}
                  onChange={(e) => {
                    updateContent("backgroundColor", "custom");
                    updateContent("customBackgroundColor", e.target.value);
                  }}
                />
                <input
                  type="text"
                  className="flex-1 p-2 border rounded"
                  placeholder="#ffffff or rgba(255,255,255,0.5)"
                  value={section.content?.customBackgroundColor || ""}
                  onChange={(e) => {
                    updateContent("backgroundColor", "custom");
                    updateContent("customBackgroundColor", e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Text Color</label>
              <div className="flex items-center">
                <input
                  type="color"
                  className="w-10 h-10 p-1 border rounded mr-2"
                  value={section.content?.customTextColor || "#000000"}
                  onChange={(e) => {
                    updateContent("textColor", "custom");
                    updateContent("customTextColor", e.target.value);
                  }}
                />
                <input
                  type="text"
                  className="flex-1 p-2 border rounded"
                  placeholder="#000000"
                  value={section.content?.customTextColor || ""}
                  onChange={(e) => {
                    updateContent("textColor", "custom");
                    updateContent("customTextColor", e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Background Image</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="URL to image"
                value={section.content?.backgroundImage || ""}
                onChange={(e) =>
                  updateContent("backgroundImage", e.target.value)
                }
              />
            </div>

            {section.content?.backgroundImage && (
              <>
                <div className="mb-4">
                  <label className="block mb-1">Background Size</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={section.content?.backgroundSize || "cover"}
                    onChange={(e) =>
                      updateContent("backgroundSize", e.target.value)
                    }
                  >
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                    <option value="auto">Auto</option>
                    <option value="100%">100%</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-1">Background Position</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={section.content?.backgroundPosition || "center"}
                    onChange={(e) =>
                      updateContent("backgroundPosition", e.target.value)
                    }
                  >
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="right">Right</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="top left">Top Left</option>
                    <option value="top right">Top Right</option>
                    <option value="bottom left">Bottom Left</option>
                    <option value="bottom right">Bottom Right</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-1">Background Repeat</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={section.content?.backgroundRepeat || "no-repeat"}
                    onChange={(e) =>
                      updateContent("backgroundRepeat", e.target.value)
                    }
                  >
                    <option value="no-repeat">No Repeat</option>
                    <option value="repeat">Repeat</option>
                    <option value="repeat-x">Repeat-X</option>
                    <option value="repeat-y">Repeat-Y</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-1">Background Attachment</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={section.content?.backgroundAttachment || "scroll"}
                    onChange={(e) =>
                      updateContent("backgroundAttachment", e.target.value)
                    }
                  >
                    <option value="scroll">Scroll</option>
                    <option value="fixed">Fixed</option>
                    <option value="local">Local</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={
                        section.content?.backgroundOverlayEnabled || false
                      }
                      onChange={(e) =>
                        updateContent(
                          "backgroundOverlayEnabled",
                          e.target.checked
                        )
                      }
                      className="mr-2"
                    />
                    <span>Background Overlay</span>
                  </label>
                </div>

                {section.content?.backgroundOverlayEnabled && (
                  <div className="ml-6 mb-4">
                    <div className="mb-2">
                      <label className="block mb-1">Overlay Color</label>
                      <div className="flex items-center">
                        <input
                          type="color"
                          className="w-10 h-10 p-1 border rounded mr-2"
                          value={
                            section.content?.backgroundOverlayColor || "#000000"
                          }
                          onChange={(e) =>
                            updateContent(
                              "backgroundOverlayColor",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="text"
                          className="flex-1 p-2 border rounded"
                          placeholder="#000000 or rgba(0,0,0,0.5)"
                          value={section.content?.backgroundOverlayColor || ""}
                          onChange={(e) =>
                            updateContent(
                              "backgroundOverlayColor",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1">Overlay Opacity</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        className="w-full"
                        value={
                          section.content?.backgroundOverlayOpacity || "0.5"
                        }
                        onChange={(e) =>
                          updateContent(
                            "backgroundOverlayOpacity",
                            e.target.value
                          )
                        }
                      />
                      <div className="flex justify-between text-xs">
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={section.content?.backgroundGradient || false}
                  onChange={(e) =>
                    updateContent("backgroundGradient", e.target.checked)
                  }
                  className="mr-2"
                />
                <span>Background Gradient</span>
              </label>
            </div>

            {section.content?.backgroundGradient && (
              <div className="ml-6 mb-4">
                <div className="mb-2">
                  <label className="block mb-1">Gradient From</label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      className="w-10 h-10 p-1 border rounded mr-2"
                      value={section.content?.gradientFrom || "#ffffff"}
                      onChange={(e) =>
                        updateContent("gradientFrom", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded"
                      placeholder="#ffffff"
                      value={section.content?.gradientFrom || ""}
                      onChange={(e) =>
                        updateContent("gradientFrom", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="mb-2">
                  <label className="block mb-1">Gradient To</label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      className="w-10 h-10 p-1 border rounded mr-2"
                      value={section.content?.gradientTo || "#000000"}
                      onChange={(e) =>
                        updateContent("gradientTo", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded"
                      placeholder="#000000"
                      value={section.content?.gradientTo || ""}
                      onChange={(e) =>
                        updateContent("gradientTo", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1">Gradient Direction</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={section.content?.gradientDirection || "to bottom"}
                    onChange={(e) =>
                      updateContent("gradientDirection", e.target.value)
                    }
                  >
                    <option value="to bottom">Top to Bottom</option>
                    <option value="to right">Left to Right</option>
                    <option value="to top">Bottom to Top</option>
                    <option value="to left">Right to Left</option>
                    <option value="to bottom right">
                      Top Left to Bottom Right
                    </option>
                    <option value="to bottom left">
                      Top Right to Bottom Left
                    </option>
                    <option value="to top right">
                      Bottom Left to Top Right
                    </option>
                    <option value="to top left">
                      Bottom Right to Top Left
                    </option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Typography Settings */}
      <div
        className={`mb-4 ${expandedSections.typography ? "" : "opacity-75"}`}
      >
        <div
          className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
          onClick={() => toggleSection("typography")}
        >
          <h5 className="font-medium">Typography</h5>
          <span>{expandedSections.typography ? "▼" : "►"}</span>
        </div>

        {expandedSections.typography && (
          <div className="p-3 border-l border-r border-b rounded-b mb-2">
            <div className="mb-4">
              <label className="block mb-1">Font Family</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.fontFamily || ""}
                onChange={(e) => updateContent("fontFamily", e.target.value)}
              >
                <option value="">Default</option>
                <option value="font-sans">Sans Serif</option>
                <option value="font-serif">Serif</option>
                <option value="font-mono">Monospace</option>
                <option value="font-display">Display</option>
                <option value="font-handwriting">Handwriting</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Font Size</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="16px or 1rem"
                value={section.content?.fontSize || ""}
                onChange={(e) => updateContent("fontSize", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-1">Font Weight</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.fontWeight || ""}
                onChange={(e) => updateContent("fontWeight", e.target.value)}
              >
                <option value="">Default</option>
                <option value="font-light">Light (300)</option>
                <option value="font-normal">Normal (400)</option>
                <option value="font-medium">Medium (500)</option>
                <option value="font-semibold">Semibold (600)</option>
                <option value="font-bold">Bold (700)</option>
                <option value="font-extrabold">Extra Bold (800)</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Line Height</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.lineHeight || ""}
                onChange={(e) => updateContent("lineHeight", e.target.value)}
              >
                <option value="">Default</option>
                <option value="leading-none">None (1)</option>
                <option value="leading-tight">Tight (1.25)</option>
                <option value="leading-snug">Snug (1.375)</option>
                <option value="leading-normal">Normal (1.5)</option>
                <option value="leading-relaxed">Relaxed (1.625)</option>
                <option value="leading-loose">Loose (2)</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Letter Spacing</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.letterSpacing || ""}
                onChange={(e) => updateContent("letterSpacing", e.target.value)}
              >
                <option value="">Default</option>
                <option value="tracking-tighter">Tighter (-0.05em)</option>
                <option value="tracking-tight">Tight (-0.025em)</option>
                <option value="tracking-normal">Normal (0)</option>
                <option value="tracking-wide">Wide (0.025em)</option>
                <option value="tracking-wider">Wider (0.05em)</option>
                <option value="tracking-widest">Widest (0.1em)</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Text Transform</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.textTransform || ""}
                onChange={(e) => updateContent("textTransform", e.target.value)}
              >
                <option value="">None</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1">Text Alignment</label>
              <div className="flex">
                <button
                  type="button"
                  className={`flex-1 p-2 border ${section.content?.alignment === "left"
                      ? "bg-blue-100 border-blue-400"
                      : "hover:bg-gray-50"
                    }`}
                  onClick={() => updateContent("alignment", "left")}
                >
                  <FontAwesomeIcon icon={faAlignLeft} />
                </button>
                <button
                  type="button"
                  className={`flex-1 p-2 border-t border-b ${section.content?.alignment === "center"
                      ? "bg-blue-100 border-blue-400"
                      : "hover:bg-gray-50"
                    }`}
                  onClick={() => updateContent("alignment", "center")}
                >
                  <FontAwesomeIcon icon={faAlignCenter} />
                </button>
                <button
                  type="button"
                  className={`flex-1 p-2 border ${section.content?.alignment === "right"
                      ? "bg-blue-100 border-blue-400"
                      : "hover:bg-gray-50"
                    }`}
                  onClick={() => updateContent("alignment", "right")}
                >
                  <FontAwesomeIcon icon={faAlignRight} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Border Settings */}
      <div className={`mb-4 ${expandedSections.border ? "" : "opacity-75"}`}>
        <div
          className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
          onClick={() => toggleSection("border")}
        >
          <h5 className="font-medium flex items-center">
            <FontAwesomeIcon icon={faBorderAll} className="mr-2" />
            Border & Shadow
          </h5>
          <span>{expandedSections.border ? "▼" : "►"}</span>
        </div>

        {expandedSections.border && (
          <div className="p-3 border-l border-r border-b rounded-b mb-2">
            <div className="mb-4">
              <label className="block mb-1">Border Width</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="1px"
                value={section.content?.borderWidth || ""}
                onChange={(e) => updateContent("borderWidth", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-1">Border Style</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.borderStyle || ""}
                onChange={(e) => updateContent("borderStyle", e.target.value)}
              >
                <option value="">None</option>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="double">Double</option>
              </select>
            </div>

            {section.content?.borderStyle &&
              section.content.borderStyle !== "" && (
                <div className="mb-4">
                  <label className="block mb-1">Border Color</label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      className="w-10 h-10 p-1 border rounded mr-2"
                      value={section.content?.borderColor || "#000000"}
                      onChange={(e) =>
                        updateContent("borderColor", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded"
                      placeholder="#000000"
                      value={section.content?.borderColor || ""}
                      onChange={(e) =>
                        updateContent("borderColor", e.target.value)
                      }
                    />
                  </div>
                </div>
              )}

            <div className="mb-4">
              <label className="block mb-1">Border Radius</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="4px"
                value={section.content?.borderRadius || ""}
                onChange={(e) => updateContent("borderRadius", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-1">Box Shadow</label>
              <select
                className="w-full p-2 border rounded"
                value={section.content?.shadow || ""}
                onChange={(e) => updateContent("shadow", e.target.value)}
              >
                <option value="">None</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra Large</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Animation settings section
  const renderAnimationSettings = () => (
    <div className={`mb-4 ${expandedSections.animation ? "" : "opacity-75"}`}>
      <div
        className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
        onClick={() => toggleSection("animation")}
      >
        <h5 className="font-medium flex items-center">
          <FontAwesomeIcon icon={faMagic} className="mr-2" />
          Animation
        </h5>
        <span>{expandedSections.animation ? "▼" : "►"}</span>
      </div>

      {expandedSections.animation && (
        <div className="p-3 border-l border-r border-b rounded-b mb-2">
          <div className="mb-4">
            <label className="block mb-1">Animation Type</label>
            <select
              className="w-full p-2 border rounded"
              value={section.content?.animation || ""}
              onChange={(e) => updateContent("animation", e.target.value)}
            >
              <option value="">None</option>
              <option value="fade-in">Fade In</option>
              <option value="slide-up">Slide Up</option>
              <option value="slide-in-left">Slide In From Left</option>
              <option value="slide-in-right">Slide In From Right</option>
              <option value="zoom-in">Zoom In</option>
              <option value="typing">Typing (for text sections)</option>
            </select>
          </div>

          {section.content?.animation && section.content.animation !== "" && (
            <>
              <div className="mb-4">
                <label className="block mb-1">Animation Delay (ms)</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  placeholder="0"
                  value={section.content?.animationDelay || "0"}
                  onChange={(e) =>
                    updateContent("animationDelay", e.target.value)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1">Animation Duration (ms)</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  placeholder="1000"
                  value={section.content?.animationDuration || "1000"}
                  onChange={(e) =>
                    updateContent("animationDuration", e.target.value)
                  }
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={section.settings?.parallaxEffect || false}
                onChange={(e) =>
                  updateSettings("parallaxEffect", e.target.checked)
                }
                className="mr-2"
              />
              <span>Parallax Effect</span>
            </label>
          </div>

          {section.settings?.parallaxEffect && (
            <div className="mb-4 ml-6">
              <label className="block mb-1">Parallax Speed</label>
              <input
                type="range"
                min="0"
                max="10"
                step="0.1"
                className="w-full"
                value={section.settings?.parallaxSpeed || "5"}
                onChange={(e) =>
                  updateSettings("parallaxSpeed", e.target.value)
                }
              />
              <div className="flex justify-between text-xs">
                <span>Slower</span>
                <span>Faster</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Responsive settings section
  const renderResponsiveSettings = (device) => {
    // Default values for each device
    let deviceInfo = {
      title: "Desktop",
      icon: faDesktop,
      breakpoint: "> 1024px",
    };

    if (device === "tablet") {
      deviceInfo = {
        title: "Tablet",
        icon: faTabletAlt,
        breakpoint: "768px - 1024px",
      };
    } else if (device === "mobile") {
      deviceInfo = {
        title: "Mobile",
        icon: faMobileAlt,
        breakpoint: "< 768px",
      };
    }

    return (
      <div className="p-3 border rounded mb-2">
        <div className="flex items-center mb-4">
          <FontAwesomeIcon icon={deviceInfo.icon} className="mr-2 text-xl" />
          <div>
            <h5 className="font-medium">{deviceInfo.title} Settings</h5>
            <p className="text-xs text-gray-500">{deviceInfo.breakpoint}</p>
          </div>
        </div>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={
                device === "mobile"
                  ? section.settings?.mobileHidden || false
                  : device === "tablet"
                    ? section.settings?.tabletHidden || false
                    : section.settings?.desktopHidden || false
              }
              onChange={(e) => {
                if (device === "mobile") {
                  updateSettings("mobileHidden", e.target.checked);
                } else if (device === "tablet") {
                  updateSettings("tabletHidden", e.target.checked);
                } else {
                  updateSettings("desktopHidden", e.target.checked);
                }
              }}
              className="mr-2"
            />
            <span>Hide on {deviceInfo.title}</span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block mb-1">Width</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="100% or 500px"
              value={getResponsiveValue(device, "width", "")}
              onChange={(e) =>
                updateResponsiveSettings(device, "width", e.target.value)
              }
            />
          </div>
          <div>
            <label className="block mb-1">Height</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="auto or 300px"
              value={getResponsiveValue(device, "height", "")}
              onChange={(e) =>
                updateResponsiveSettings(device, "height", e.target.value)
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-1">Font Size</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="16px or 1rem"
            value={getResponsiveValue(device, "fontSize", "")}
            onChange={(e) =>
              updateResponsiveSettings(device, "fontSize", e.target.value)
            }
          />
        </div>

        <div className="mb-4">
          <label className="block mb-1">Padding</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="20px or 2rem"
            value={getResponsiveValue(device, "padding", "")}
            onChange={(e) =>
              updateResponsiveSettings(device, "padding", e.target.value)
            }
          />
        </div>

        <div className="mb-4">
          <label className="block mb-1">Margin</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="20px or 2rem"
            value={getResponsiveValue(device, "margin", "")}
            onChange={(e) =>
              updateResponsiveSettings(device, "margin", e.target.value)
            }
          />
        </div>
      </div>
    );
  };

  // Advanced settings section
  const renderAdvancedSettings = () => (
    <div className={`mb-4 ${expandedSections.advanced ? "" : "opacity-75"}`}>
      <div
        className="flex justify-between items-center cursor-pointer px-2 py-2 bg-gray-50 hover:bg-gray-100 rounded"
        onClick={() => toggleSection("advanced")}
      >
        <h5 className="font-medium flex items-center">
          <FontAwesomeIcon icon={faCog} className="mr-2" />
          Advanced Settings
        </h5>
        <span>{expandedSections.advanced ? "▼" : "►"}</span>
      </div>

      {expandedSections.advanced && (
        <div className="p-3 border-l border-r border-b rounded-b mb-2">
          <div className="mb-4">
            <label className="block mb-1">Custom CSS Class</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="my-custom-class"
              value={section.settings?.containerClass || ""}
              onChange={(e) => updateSettings("containerClass", e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block mb-1">Section ID</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="my-section-id"
              value={section.settings?.sectionId || ""}
              onChange={(e) => updateSettings("sectionId", e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Use this ID to create anchor links to this section
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-1">Custom CSS</label>
            <textarea
              className="w-full p-2 border rounded font-mono text-sm"
              placeholder="color: red; font-weight: bold;"
              value={section.settings?.customCss || ""}
              onChange={(e) => updateSettings("customCss", e.target.value)}
              rows="3"
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">
              Enter CSS rules in the format: property: value;
            </p>
          </div>

          {/* Accessibility Settings */}
          <div className="mb-4">
            <label className="block mb-1">ARIA Label</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="Description for screen readers"
              value={section.settings?.accessibility?.ariaLabel || ""}
              onChange={(e) => {
                const currentAccessibility =
                  section.settings?.accessibility || {};
                updateSettings("accessibility", {
                  ...currentAccessibility,
                  ariaLabel: e.target.value,
                });
              }}
            />
          </div>

          <div className="mb-4">
            <label className="block mb-1">ARIA Role</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="banner, contentinfo, etc."
              value={section.settings?.accessibility?.ariaRole || ""}
              onChange={(e) => {
                const currentAccessibility =
                  section.settings?.accessibility || {};
                updateSettings("accessibility", {
                  ...currentAccessibility,
                  ariaRole: e.target.value,
                });
              }}
            />
          </div>

          <div className="mb-4">
            <label className="block mb-1">Tab Index</label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              placeholder="0"
              value={section.settings?.accessibility?.tabIndex || ""}
              onChange={(e) => {
                const currentAccessibility =
                  section.settings?.accessibility || {};
                updateSettings("accessibility", {
                  ...currentAccessibility,
                  tabIndex: e.target.value,
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Debug info */}
      <div className="bg-gray-100 p-2 text-xs mb-4">
        <p>Section type: {section.type}</p>
        <p>Page slug: {pageSlug || "none"}</p>
        {section.parentId && <p>Parent ID: {section.parentId}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Section specific form */}
        <div className="lg:col-span-2">{renderSectionForm()}</div>

        {/* Right column: Settings tabs */}
        <div className="border rounded p-4 bg-white">
          <div className="mb-4 flex border-b">
            <button
              className={`px-4 py-2 ${activeSettingsTab === "layout"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("layout")}
            >
              Layout
            </button>
            <button
              className={`px-4 py-2 ${activeSettingsTab === "appearance"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("appearance")}
            >
              Style
            </button>
            <button
              className={`px-4 py-2 ${activeSettingsTab === "advanced"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("advanced")}
            >
              Advanced
            </button>
          </div>

          {/* Device selector for responsive settings */}
          <div className="mb-4 flex justify-center space-x-2">
            <button
              className={`p-2 ${activeSettingsTab === "desktop"
                  ? "bg-blue-100 text-blue-600 border border-blue-300 rounded"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("desktop")}
              title="Desktop Settings"
            >
              <FontAwesomeIcon icon={faDesktop} />
            </button>
            <button
              className={`p-2 ${activeSettingsTab === "tablet"
                  ? "bg-blue-100 text-blue-600 border border-blue-300 rounded"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("tablet")}
              title="Tablet Settings"
            >
              <FontAwesomeIcon icon={faTabletAlt} />
            </button>
            <button
              className={`p-2 ${activeSettingsTab === "mobile"
                  ? "bg-blue-100 text-blue-600 border border-blue-300 rounded"
                  : "text-gray-600"
                }`}
              onClick={() => setTab("mobile")}
              title="Mobile Settings"
            >
              <FontAwesomeIcon icon={faMobileAlt} />
            </button>
          </div>

          {/* Settings content based on active tab */}
          <div className="overflow-y-auto max-h-screen">
            {renderSettings()}

            {/* Animation Settings - Always visible in Style tab */}
            {activeSettingsTab === "appearance" && renderAnimationSettings()}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded mr-2"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          onClick={onSave}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default SectionForm;
